import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import useHomeContext from '@/hooks/useHomeContext';
import { routes } from '@/utils/routes';
import { LAST_7_DAYS } from './useDateFilter';

/**
 * Resets account selection and transaction filters back to their Home defaults, without
 * navigating - used by the Home breadcrumb, which handles navigation itself via its own path.
 */
export const useResetHomeState = () => {
  const {
    setBankAccount,
    setSelectedAccount,
    setAccountsFilter,
    setDateFilter,
    setTransactionFilter,
    setTeamMemberFilter,
    allAccounts,
    defaultOption
  } = useHomeContext()!;

  return useCallback(() => {
    const today = moment().endOf('day').toDate();
    const sevenDaysAgo = moment(today).subtract(7, 'days').startOf('day').toDate();

    setBankAccount(allAccounts);
    setSelectedAccount(defaultOption);
    setAccountsFilter([]);
    setDateFilter({
      startDate: sevenDaysAgo,
      endDate: today,
      rangeName: { label: LAST_7_DAYS, value: LAST_7_DAYS }
    });
    setTransactionFilter([]);
    setTeamMemberFilter([]);
  }, [
    allAccounts,
    defaultOption,
    setBankAccount,
    setSelectedAccount,
    setAccountsFilter,
    setDateFilter,
    setTransactionFilter,
    setTeamMemberFilter
  ]);
};

/**
 * Resets Home's account/filter state and navigates there - for Close/exit actions on pages
 * (e.g. Pay, Transfer) that can be entered scoped to a selected account, so Home doesn't reopen
 * still filtered to that account.
 */
export const useNavigateHome = () => {
  const navigate = useNavigate();
  const resetHomeState = useResetHomeState();

  return useCallback(() => {
    resetHomeState();
    navigate(routes.home);
  }, [resetHomeState, navigate]);
};
